import { Expose, Type, Exclude } from 'class-transformer';
import { Point, Feature } from 'geojson';
import { Observable, Subject } from 'rxjs';

import { ExposeRawData } from '@lib/common/decorators';
import { SnModel } from '@lib/common/models';

import { SnOrganization } from '../organization';
import { FsTipDisposition } from './tip-disposition';
import { SnTipMedia } from './tip-media';
import { SnTipNote } from './tip-note';

export class SnTip extends SnModel {
  @Expose()
  public id: string;

  @Expose()
  @Type(() => SnOrganization)
  public organizations: SnOrganization;

  @Expose()
  public phone: string;

  @Expose()
  public name: string;

  @Expose()
  public email: string;

  @Expose()
  public description: string;

  @Expose()
  @Type(() => FsTipDisposition)
  public disposition: FsTipDisposition;

  @Expose()
  public anonymous: boolean;

  @Expose({ name: 'vaultIncidents' })
  public vaults: any[];

  @Expose()
  @ExposeRawData()
  public geometry: Point;

  @Expose({ name: 'note' })
  @Type(() => SnTipNote)
  public notes: SnTipNote[];

  @Expose()
  @Type(() => SnTipMedia)
  public media: SnTipMedia[];

  @Expose()
  public status: string;

  @ExposeRawData()
  public integrationMeta;

  public isDownloadingMedia: boolean = false;
  public fileSize: number = 0;
  public downloadedSize: number = 0;
  public percentDone: number = 0;
  public secondsLeft: number = 0;

  @Expose({ name: 'createdTimestamp' })
  @Type(() => Date)
  @Exclude({ toPlainOnly: true })
  public createdAt: Date;

  @Expose({ name: 'updatedTimestamp' })
  @Type(() => Date)
  public updatedAt: Date;

  @Expose()
  public fileIsAvailable: boolean;

  @Expose()
  public altitude: number;

  @Expose()
  public heading: number;

  @Expose()
  public publicToken: string;

  @ExposeRawData()
  public note: any;

  @Expose()
  public isOpen: boolean;

  @Expose()
  public address: string;

  @Expose()
  public city: string;

  @Expose()
  public state: string;

  @Expose()
  public zip: string;

  @ExposeRawData()
  public mapViews: any;

  @Expose()
  public type: number;

  @Expose()
  public officerFirstName: string;

  @Expose()
  public officerLastName: string;

  @Expose()
  public officerBadge: string;

  @Expose()
  public arrestedFirstName: string;

  @Expose()
  public arrestedLastName: string;

  @Expose()
  @Type(() => Date)
  public arrestedDob: Date;

  @Expose()
  public tipLink: string;

  private readonly _cancelDownload$ = new Subject<void>();

  constructor(data: any = {}) {
    super();

    this.fromJSON(data);
  }

  get coords(): { lng: number; lat: number } {
    if (!this.geometry) {
      return;
    }

    return {
      lng: this.geometry.coordinates[0],
      lat: this.geometry.coordinates[1],
    };
  }

  get cancelDownload$(): Observable<void> {
    return this._cancelDownload$.asObservable();
  }

  public toGeoJSON(): Feature {
    return {
      type: 'Feature',
      id: this.id,
      geometry: this.geometry,
      properties: {
        name: this.phone,
        id: this.id,
      },
    };
  }

  public cancelDownload(): void {
    this._cancelDownload$.next();
    this.isDownloadingMedia = false;
  }
}
